import { GAME_RULES, multiplierFor } from "./rules";
import type {
  GameState,
  LevelId,
  RuntimeGameEventDefinition,
  SessionSummary,
} from "./types";

export function createInitialState(levelId: LevelId): GameState {
  return {
    levelId,
    status: "playing",
    hearts: 3,
    shieldActive: false,
    score: 0,
    streak: 0,
    multiplier: 1,
    maxStreak: 0,
    maxMultiplier: 1,
    correctKeys: 0,
    totalKeys: 0,
    spawnedCount: 0,
    processedCount: 0,
    resolvedCount: 0,
    targetInstanceId: null,
    discoveryKeys: [],
  };
}

export function registerSpawn(state: GameState): GameState {
  return { ...state, spawnedCount: state.spawnedCount + 1 };
}

export function registerKey(state: GameState, correct: boolean): GameState {
  return {
    ...state,
    totalKeys: state.totalKeys + 1,
    correctKeys: state.correctKeys + (correct ? 1 : 0),
  };
}

export function selectTarget(
  state: GameState,
  instanceId: string | null,
): GameState {
  return { ...state, targetInstanceId: instanceId };
}

export function resolveEvent(
  state: GameState,
  event: RuntimeGameEventDefinition,
): GameState {
  let streak = state.streak + 1;
  const scoringMultiplier = multiplierFor(streak);
  let score = state.score + event.basePoints * scoringMultiplier;
  let hearts = state.hearts;
  let shieldActive = state.shieldActive;

  switch (event.reward.type) {
    case "shield":
      if (shieldActive) score += GAME_RULES.saturatedRewardPoints;
      else shieldActive = true;
      break;
    case "heal":
      if (hearts === GAME_RULES.maxHearts)
        score += GAME_RULES.saturatedRewardPoints;
      else hearts = (hearts + event.reward.amount) as GameState["hearts"];
      break;
    case "bonus_points":
      score += event.reward.amount;
      break;
    case "combo_boost":
      streak += event.reward.amount;
      break;
    case "none":
      break;
  }

  const multiplier = multiplierFor(streak);
  const discoveryKey = `${event.response.product}\u0000${event.response.message}`;
  const discoveryKeys = state.discoveryKeys.includes(discoveryKey)
    ? state.discoveryKeys
    : [...state.discoveryKeys, discoveryKey];
  const processedCount = state.processedCount + 1;

  return {
    ...state,
    hearts,
    shieldActive,
    score,
    streak,
    multiplier,
    maxStreak: Math.max(state.maxStreak, streak),
    maxMultiplier: Math.max(
      state.maxMultiplier,
      multiplier,
    ) as GameState["maxMultiplier"],
    processedCount,
    resolvedCount: state.resolvedCount + 1,
    targetInstanceId: null,
    discoveryKeys,
    status: processedCount === GAME_RULES.totalEvents ? "won" : state.status,
  };
}

export function expireEvent(
  state: GameState,
  event: RuntimeGameEventDefinition,
): GameState {
  const processedCount = state.processedCount + 1;
  if (event.type !== "threat") {
    return {
      ...state,
      processedCount,
      targetInstanceId: state.targetInstanceId,
      status: processedCount === GAME_RULES.totalEvents ? "won" : state.status,
    };
  }

  const shieldActive = state.shieldActive;
  const hearts = shieldActive
    ? state.hearts
    : ((state.hearts - 1) as GameState["hearts"]);
  return {
    ...state,
    shieldActive: false,
    hearts,
    streak: 0,
    multiplier: 1,
    processedCount,
    targetInstanceId: state.targetInstanceId,
    status:
      hearts === 0
        ? "lost"
        : processedCount === GAME_RULES.totalEvents
          ? "won"
          : state.status,
  };
}

export function accuracyFor(
  state: Pick<GameState, "correctKeys" | "totalKeys">,
): number {
  return state.totalKeys === 0
    ? 0
    : Math.round((state.correctKeys / state.totalKeys) * 100);
}

export function discoveriesFor(
  state: GameState,
): SessionSummary["discoveries"] {
  return state.discoveryKeys.slice(0, 3).map((key) => {
    const [product = "", message = ""] = key.split("\u0000");
    return { product, message };
  });
}
